import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { collection, getDocs, query, orderBy, limit, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActionArea,
  Divider,
  CircularProgress,
  Chip,
  Button
} from '@mui/material';
import { Item } from '../types/item';
import { Collection } from '../types/collection';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon } from '@mui/icons-material';

interface RecentItem extends Item {
  collectionName?: string;
}

export default function DashboardLayout() {
  const { userId } = useAuth();
  const navigate = useNavigate();
  const [recentItems, setRecentItems] = useState<RecentItem[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch collections and recent items across all collections
  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // First get all collections
        const collectionsRef = collection(db, `users/${userId}/collections`);
        const collectionsSnapshot = await getDocs(collectionsRef);
        const collectionsData = collectionsSnapshot.docs.map(doc => ({ 
          ...doc.data(), 
          id: doc.id 
        })) as Collection[];
        setCollections(collectionsData);
        
        // Then get recent items from all collections
        const allItems: RecentItem[] = [];
        
        // Using Promise.all to fetch items from all collections in parallel
        await Promise.all(collectionsData.map(async (coll) => {
          const itemsRef = collection(db, `users/${userId}/collections/${coll.id}/items`);
          const q = query(itemsRef, orderBy('createdAt', 'desc'), limit(10));
          const itemsSnapshot = await getDocs(q);
          
          const collectionItems = itemsSnapshot.docs.map(doc => ({
            ...doc.data(),
            id: doc.id,
            collectionName: coll.name,
            collectionId: coll.id
          })) as RecentItem[];
          
          allItems.push(...collectionItems);
        }));
        
        // Sort all items by creation date (newest first) and take the top 10
        const sortedItems = allItems.sort((a, b) => {
          // Handle Firestore Timestamp objects
          const dateA = a.createdAt instanceof Timestamp ? 
            a.createdAt.toDate().getTime() : 
            (a.createdAt as any)?.toDate?.()?.getTime() || 0;
            
          const dateB = b.createdAt instanceof Timestamp ? 
            b.createdAt.toDate().getTime() : 
            (b.createdAt as any)?.toDate?.()?.getTime() || 0;
            
          return dateB - dateA;
        }).slice(0, 10);
        
        setRecentItems(sortedItems);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [userId]);
  
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown date';
    
    const date = timestamp instanceof Timestamp ? 
      timestamp.toDate() : new Date(timestamp);
      
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={0} sx={{ p: 3, bgcolor: 'error.light', color: 'error.dark', mb: 3 }}>
        <Typography>{error}</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome to TrackMyTrove. Manage your collections and items all in one place.
        </Typography>
      </Box>

      {/* Collections Summary */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" component="h2">
            Your Collections
          </Typography>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/h/collections')}
          >
            View All Collections
          </Button>
        </Box>
        
        <Typography variant="body1" sx={{ mb: 2 }}>
          You have {collections.length} collection{collections.length !== 1 ? 's' : ''}
        </Typography>
      </Paper>

      {/* Recent Items */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          Recent Items
        </Typography>
        
        {recentItems.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              No items found. Add items to your collections to see them here.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {recentItems.map(item => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    height: '100%', 
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: 6
                    }
                  }}
                >
                  <CardActionArea 
                    onClick={() => navigate(`/h/collection/${item.collectionId}/item/${item.id}`)}
                    sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
                  >
                    <CardMedia
                      component="div"
                      sx={{
                        height: 140,
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundSize: 'contain',
                        backgroundPosition: 'center'
                      }}
                      image={item.imageUrl || item.images?.[0]?.url || undefined}
                    >
                      {!item.imageUrl && !item.images?.[0]?.url && (
                        <ImageIcon sx={{ fontSize: 60, color: 'rgba(0, 0, 0, 0.2)' }} />
                      )}
                    </CardMedia>
                    
                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" component="div" gutterBottom noWrap>
                        {item.name}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {item.description || 'No description'}
                      </Typography>
                      
                      <Box sx={{ mt: 'auto' }}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="caption" color="primary" gutterBottom>
                          {item.collectionName}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          Added {formatDate(item.createdAt)}
                        </Typography>
                        
                        {item.tags && item.tags.length > 0 && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {item.tags.slice(0, 3).map((tag, idx) => (
                              <Chip 
                                key={idx} 
                                label={tag} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                  height: '20px',
                                  '& .MuiChip-label': {
                                    px: 1,
                                    fontSize: '0.625rem'
                                  }
                                }}
                              />
                            ))}
                            {item.tags.length > 3 && (
                              <Chip 
                                label={`+${item.tags.length - 3}`} 
                                size="small"
                                sx={{ 
                                  height: '20px',
                                  '& .MuiChip-label': {
                                    px: 1,
                                    fontSize: '0.625rem'
                                  }
                                }}
                              />
                            )}
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </Box>
  );
}